import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { switchMap, refCount, map, publishReplay } from 'rxjs/operators';
import { max, uniq, includes, union } from 'lodash';
import { GridOptions } from 'ag-grid-community';

import { SponsorStudySitesStorage } from './sponsor-study-sites-storage';
import { SponsorRouteData } from '../sponsor-route-data';
import { comparators, UrlBuilder } from '../../core';
import { SponsorStudySite } from './sponsor-study-site';
import { CovanceGridColumn } from '../../covance-ag-grid';
import { BarRendererComponent, BarParams } from '../../covance-ag-grid';
import { SponsorStudy } from '../../sponsor-dashboard/sponsor-study';
import { UserPermissions } from '../../user-permissions/user-permissions';

export interface StudySiteRowData {
    data: SponsorStudySite;
}

@Component({
    selector: 'study-sites',
    template: require('./study-sites.component.html'),
    styles: [require('./study-sites.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StudySitesComponent {
    public gridOptions: GridOptions;
    public columnDefs$: Observable<CovanceGridColumn[]>;
    public data$: Observable<SponsorStudySite[]>;
    public allCountries$: Observable<string[]>;
    public allRegions$: Observable<string[]>;
    public countriesFilterSubject: BehaviorSubject<string[]>;
    public regionsFilterSubject: BehaviorSubject<string[]>;

    constructor(
        private sitesStorage: SponsorStudySitesStorage,
        private sponsorRouteData: SponsorRouteData,
        private urlBuilder: UrlBuilder
    ) {
        this.countriesFilterSubject = new BehaviorSubject([]);
        this.regionsFilterSubject = new BehaviorSubject([]);
        this.gridOptions = {
            enableFilter: false,
            floatingFilter: false,
            rowHeight: 27,
            enableSorting: true,
            floatingFiltersHeight: 32
        };

        const rawData$: Observable<SponsorStudySite[]> = combineLatest([
            this.sponsorRouteData.selectedStudy.pipe(
                switchMap(st => this.sitesStorage.loadSponsorStudySites(st.protocolId)),
            ),
            this.countriesFilterSubject,
            this.regionsFilterSubject
        ]).pipe(
            map(([sites, countriesFilter, regionFilter]) => {
                const countryFilterDefined = countriesFilter && countriesFilter.length > 0;
                const regionFilterDefined = regionFilter && regionFilter.length > 0;

                const sitesFiltered = sites.filter(s =>
                    (!countryFilterDefined || includes(countriesFilter, s.country))
                    &&
                    (!regionFilterDefined || includes(regionFilter, s.region))
                );

                return sitesFiltered;
            }),
            publishReplay(),
            refCount()
        );

        this.allCountries$ = rawData$.pipe(
            map((sites) => uniq(sites.map(s => s.country).filter(s => s)))
        );

        this.allRegions$ = rawData$.pipe(
            map((sites) => uniq(sites.map(s => s.region).filter(s => s)))
        );

        this.data$ = rawData$;

        this.columnDefs$ = combineLatest([
            rawData$,
            this.sponsorRouteData.selectedStudy,
            this.sponsorRouteData.studyPermissions
        ]).pipe(
            map(([sites, study, permissions]) => this.getGridColumns(sites, study, permissions))
        );
    }

    public onCountryFilterChanged($event: string[]) {
        this.countriesFilterSubject.next($event);
    }

    public onRegionFilterChanged($event: string[]) {
        this.regionsFilterSubject.next($event);
    }

    private getGridColumns(sites: SponsorStudySite[], study: SponsorStudy, permissions: UserPermissions): CovanceGridColumn[] {
        const maxUnreadCount = max(union(
            sites.map(s => s.unreadResultAlertsCount),
            sites.map(s => s.unreadSiteCommunications),
            sites.map(s => s.unreadSiteActionsCount)
        ));

        const barColumnComparator = (a: BarParams, b: BarParams) => {
            return comparators.numberComparator(a ? a.value : null, b ? b.value : null);
        };

        const appendResultAlertsLink = permissions.canViewActivityReport('result-alerts');
        const appendSiteActionsLink = permissions.canViewActivityReport('site-actions');
        const appendSiteCommLink = permissions.canViewActivityReport('site-communications');

        const columns: CovanceGridColumn[] = [
            {
                headerName: 'Investigator',
                field: 'investigatorFullName',
                suppressFilter: true
            },
            {
                headerName: 'Site',
                field: 'siteNumber',
                filter: 'agSetColumnFilter'
            },
            {
                headerName: 'Subjects',
                field: 'subjectsCount'
            },
            {
                headerName: 'Country',
                field: 'country'
            },
            {
                headerName: 'Region',
                field: 'region'
            },
            {
                headerName: 'Unread Alerts',
                field: 'unreadResultAlertsCount',
                suppressFilter: true,
                cellRendererFramework: BarRendererComponent,
                valueGetter: (params: StudySiteRowData): BarParams => {
                    return {
                        appendLink: appendResultAlertsLink && params.data.unreadResultAlertsCount > 0,
                        routerLink: appendResultAlertsLink ?
                            this.urlBuilder
                                .esa()
                                .sponsor()
                                .study(study)
                                .activityReports()
                                .documents('result-alerts')
                                .url() : null,
                        queryParams: appendResultAlertsLink ?
                            { unreadOnly: true, site: JSON.stringify([params.data.siteId.toString()]) }
                            : null,
                        threshold: maxUnreadCount,
                        value: params.data.unreadResultAlertsCount
                    };
                },
                filterValueGetter: d => d.data.unreadResultAlertsCount,
                comparator: barColumnComparator
            },
            {
                headerName: 'Unread Site Actions',
                field: 'unreadSiteActionsCount',
                suppressFilter: true,
                cellRendererFramework: BarRendererComponent,
                valueGetter: (params: StudySiteRowData): BarParams => {
                    return {
                        appendLink: appendSiteActionsLink && params.data.unreadSiteActionsCount > 0,
                        routerLink: appendSiteActionsLink ?
                            this.urlBuilder
                                .esa()
                                .sponsor()
                                .study(study)
                                .activityReports()
                                .documents('site-actions')
                                .url() : null,
                        queryParams: appendSiteActionsLink ?
                            { unreadOnly: true, site: JSON.stringify([params.data.siteId.toString()]) }
                            : null,
                        threshold: maxUnreadCount,
                        value: params.data.unreadSiteActionsCount
                    };
                },
                filterValueGetter: d => d.data.unreadSiteActionsCount,
                comparator: barColumnComparator
            },
            {
                headerName: 'Unread Site Communications',
                field: 'unreadSiteCommunications',
                suppressFilter: true,
                cellRendererFramework: BarRendererComponent,
                valueGetter: (params: StudySiteRowData): BarParams => {
                    return {
                        appendLink: appendSiteCommLink && params.data.unreadSiteCommunications > 0,
                        routerLink: appendSiteCommLink ?
                            this.urlBuilder
                                .esa()
                                .sponsor()
                                .study(study)
                                .activityReports()
                                .documents('site-communications')
                                .url() : null,
                        queryParams: appendSiteCommLink ?
                            { unreadOnly: true, site: JSON.stringify([params.data.siteId.toString()]) }
                            : null,
                        threshold: maxUnreadCount,
                        value: params.data.unreadSiteCommunications
                    };
                },
                filterValueGetter: d => d.data.unreadSiteCommunications,
                comparator: barColumnComparator
            }
        ];

        return columns;
    }
}
